import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import {
    ChartBar, ChartLine, Warning, CheckCircle, TrendUp, Info as InfoIcon,
} from "@phosphor-icons/react";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    ReferenceLine, Cell,
} from "recharts";

/* ═══════════════════════════════════════════════════════════════════════
 * RailSixSigmaPanel — shared bottom-of-dashboard analytics for all four
 * rail dashboards. Renders per-leg cycle-time μ / σ / Cpk / Sigma stats,
 * a per-consignment cycle breakdown table (point-to-point minutes), and
 * a colour-coded production-recommendations list.
 * ═══════════════════════════════════════════════════════════════════════ */

const SIGMA_TONE = (sigma) => {
    if (sigma == null) return { bg: "#F1F5F9", fg: "#334155", label: "—" };
    if (sigma >= 6)     return { bg: "#D1FAE5", fg: "#047857", label: "6σ+ Excellent" };
    if (sigma >= 4.5)   return { bg: "#DBEAFE", fg: "#1D4ED8", label: "4.5-6σ Capable" };
    if (sigma >= 3)     return { bg: "#FEF3C7", fg: "#92400E", label: "3-4.5σ Marginal" };
    return                { bg: "#FEE2E2", fg: "#991B1B", label: "< 3σ Incapable" };
};

const CPK_TONE = (cpk) => {
    if (cpk == null) return "text-slate-400";
    if (cpk >= 1.33) return "text-emerald-600";
    if (cpk >= 1.0)  return "text-amber-600";
    return              "text-red-600";
};

const fmtMin = (v) => {
    if (v == null) return "—";
    if (v < 60) return `${Math.round(v)} m`;
    const h = Math.floor(v / 60);
    const m = Math.round(v % 60);
    return `${h}h ${m}m`;
};

const fmtDt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short" }); }
    catch { return iso; }
};

function SeverityBadge({ severity }) {
    const tone = {
        high:   { bg: "#FEE2E2", fg: "#991B1B", Icon: Warning,       label: "PRIORITY" },
        medium: { bg: "#FEF3C7", fg: "#92400E", Icon: TrendUp,       label: "ACTION" },
        info:   { bg: "#D1FAE5", fg: "#065F46", Icon: CheckCircle,   label: "STABLE" },
    }[severity] || { bg: "#F1F5F9", fg: "#334155", Icon: InfoIcon, label: severity };
    const { Icon } = tone;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest"
              style={{ backgroundColor: tone.bg, color: tone.fg }}>
            <Icon size={10} weight="fill" />
            {tone.label}
        </span>
    );
}

export default function RailSixSigmaPanel({ role = "overview" }) {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/rail/six-sigma", { params: { days } });
            setData(r.data);
            setErr(null);
        } catch (e) {
            setErr(formatApiError(e));
        } finally { setLoading(false); }
    }, [days]);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const chartData = useMemo(() => (data?.legs || []).map((l) => ({
        name: l.label.replace(" →", " →\n"),
        short: l.key.replaceAll("_", " "),
        mean: l.mean ?? 0,
        target: l.target_min ?? 0,
        std: l.std ?? 0,
        cpk: l.cpk ?? 0,
    })), [data]);

    if (err) {
        return (
            <div className="g-card p-4" data-testid="rail-six-sigma-error">
                <div className="text-sm text-red-700">Six-Sigma analysis unavailable: {err}</div>
            </div>
        );
    }

    return (
        <div className="g-card p-4 space-y-4" data-testid={`rail-six-sigma-${role}`}>
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <ChartLine size={22} weight="bold" className="text-indigo-600 flex-shrink-0" />
                    <div>
                        <div className="text-lg font-black text-[var(--grindrod-navy)] leading-tight">
                            Live Six-Sigma Analysis · Rail Cycle
                        </div>
                        <div className="text-[11px] text-slate-500 tracking-wide uppercase font-semibold">
                            Point-to-point cycle time · Cpk & Sigma level · production recommendations
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Window</label>
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                            className="g-input !py-1 !px-2 text-xs w-auto"
                            data-testid="rail-six-sigma-days">
                        <option value={7}>7 days</option>
                        <option value={30}>30 days</option>
                        <option value={90}>90 days</option>
                        <option value={180}>6 months</option>
                    </select>
                    <div className="text-[10px] text-slate-500 font-semibold">
                        {data?.consignments || 0} consignments · {data?.completed || 0} completed
                    </div>
                </div>
            </div>

            {loading && !data && (
                <div className="text-center py-8 text-slate-400 text-sm italic">Computing cycle statistics…</div>
            )}

            {data && (
                <>
                    {/* Per-leg stats table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="rail-six-sigma-legs">
                            <thead>
                                <tr className="bg-slate-100 text-slate-700 uppercase text-[10px] tracking-widest">
                                    <th className="text-left px-2 py-2">Cycle leg</th>
                                    <th className="text-right px-2 py-2">n</th>
                                    <th className="text-right px-2 py-2">μ mean</th>
                                    <th className="text-right px-2 py-2">σ std dev</th>
                                    <th className="text-right px-2 py-2">Min · Max</th>
                                    <th className="text-right px-2 py-2">Target</th>
                                    <th className="text-right px-2 py-2">% over target</th>
                                    <th className="text-right px-2 py-2">Cpk</th>
                                    <th className="text-center px-2 py-2">Sigma level</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.legs || []).map((l) => {
                                    const tone = SIGMA_TONE(l.sigma_level);
                                    return (
                                        <tr key={l.key} className="border-b border-slate-100 hover:bg-slate-50">
                                            <td className="px-2 py-1.5 font-semibold text-slate-800">{l.label}</td>
                                            <td className="px-2 py-1.5 text-right font-mono">{l.n}</td>
                                            <td className="px-2 py-1.5 text-right font-mono">{l.mean != null ? fmtMin(l.mean) : "—"}</td>
                                            <td className="px-2 py-1.5 text-right font-mono">{l.std != null ? fmtMin(l.std) : "—"}</td>
                                            <td className="px-2 py-1.5 text-right font-mono text-[10px] text-slate-500">
                                                {l.min != null ? `${fmtMin(l.min)} · ${fmtMin(l.max)}` : "—"}
                                            </td>
                                            <td className="px-2 py-1.5 text-right font-mono text-slate-500">{fmtMin(l.target_min)}</td>
                                            <td className={`px-2 py-1.5 text-right font-mono ${l.over_target_pct > 20 ? "text-red-600 font-bold" : "text-slate-600"}`}>
                                                {l.n > 0 ? `${l.over_target_pct}%` : "—"}
                                            </td>
                                            <td className={`px-2 py-1.5 text-right font-mono font-bold ${CPK_TONE(l.cpk)}`}>
                                                {l.cpk ?? "—"}
                                            </td>
                                            <td className="px-2 py-1.5 text-center">
                                                <span className="inline-block px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider"
                                                      style={{ backgroundColor: tone.bg, color: tone.fg }}>
                                                    {tone.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Chart — mean vs target per leg */}
                    {chartData.some((d) => d.mean > 0) && (
                        <div className="h-56 border border-slate-200 rounded bg-white p-2" data-testid="rail-six-sigma-chart">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-2 pb-1">
                                Mean cycle time vs target (minutes)
                            </div>
                            <ResponsiveContainer width="100%" height="90%">
                                <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 20, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                    <XAxis dataKey="short" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
                                    <YAxis tick={{ fontSize: 10 }} label={{ value: "min", position: "insideLeft", angle: -90, style: { fontSize: 10 } }} />
                                    <Tooltip formatter={(v, k) => [`${v} min`, k === "mean" ? "μ mean" : k]} />
                                    <Bar dataKey="mean" name="μ mean">
                                        {chartData.map((d, i) => (
                                            <Cell key={i} fill={d.mean > d.target ? "#EF4444" : "#2563EB"} />
                                        ))}
                                    </Bar>
                                    <Bar dataKey="target" name="Target" fill="#94A3B8" fillOpacity={0.4} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Per-consignment breakdown */}
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1">
                            Consignment Cycle Breakdown (most recent {data.consignment_breakdown?.length || 0})
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-[11px]" data-testid="rail-six-sigma-breakdown">
                                <thead>
                                    <tr className="bg-slate-100 text-slate-700 uppercase text-[9px] tracking-widest">
                                        <th className="text-left px-2 py-2">Consignment</th>
                                        <th className="text-left px-2 py-2">Mine</th>
                                        <th className="text-right px-2 py-2">Wagons</th>
                                        {(data.legs || []).map((l) => (
                                            <th key={l.key} className="text-right px-2 py-2" title={l.label}>
                                                {l.label.split("→").slice(-1)[0].trim()}
                                            </th>
                                        ))}
                                        <th className="text-right px-2 py-2 bg-slate-200">Total cycle</th>
                                        <th className="text-right px-2 py-2">Completed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.consignment_breakdown || []).map((c) => (
                                        <tr key={c.consignment_no} className="border-b border-slate-100 hover:bg-slate-50">
                                            <td className="px-2 py-1.5 font-mono font-semibold">{c.consignment_no}</td>
                                            <td className="px-2 py-1.5 truncate max-w-[120px]">{c.mine_name || "—"}</td>
                                            <td className="px-2 py-1.5 text-right font-mono">
                                                {c.wagons_tipped || 0}/{c.wagons || 0}
                                            </td>
                                            {(c.legs || []).map((leg, i) => (
                                                <td key={i} className={`px-2 py-1.5 text-right font-mono
                                                    ${leg.over_target ? "text-red-600 font-bold" : "text-slate-700"}`}>
                                                    {leg.minutes != null ? fmtMin(leg.minutes) : "—"}
                                                </td>
                                            ))}
                                            <td className="px-2 py-1.5 text-right font-mono font-bold bg-slate-50">
                                                {c.total_cycle_min != null ? fmtMin(c.total_cycle_min) : "—"}
                                            </td>
                                            <td className="px-2 py-1.5 text-right font-mono text-slate-500">
                                                {fmtDt(c.completed_at)}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!data.consignment_breakdown || !data.consignment_breakdown.length) && (
                                        <tr><td colSpan={5 + (data.legs?.length || 0) + 1}
                                            className="px-2 py-6 text-center text-slate-400 italic">
                                            No completed consignments in the selected window.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Production recommendations */}
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1">
                            <ChartBar size={14} weight="bold" />
                            Production Recommendations
                        </div>
                        <ul className="space-y-1" data-testid="rail-six-sigma-recommendations">
                            {(data.recommendations || []).map((r, i) => (
                                <li key={i} className="flex items-start gap-2 p-2 rounded bg-slate-50 border border-slate-200">
                                    <SeverityBadge severity={r.severity} />
                                    <div className="text-xs text-slate-800 flex-1">
                                        <b>{r.leg}:</b> {r.text}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            )}
        </div>
    );
}
